import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/f0-react'
import {
  OneEmptyState,
  RichTextDisplay,
  SectionHeader,
} from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'
import classNames from 'classnames'
import { DataCatalogQuery } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { CollapsibleTeaserBlock } from 'modules/ai_reports/components/CollapsibleTeaserBlock'
import DataCatalogList from 'modules/ai_reports/components/DataCatalogSelector/components/DataCatalogList'
import SourceDetail from 'modules/ai_reports/components/DataCatalogSelector/components/SourceDetail'
import { getSlugFromTableId } from 'modules/ai_reports/lib/slug-tableid-mapping'

export type DataCatalogSelectorProps = {
  showActions?: boolean
  sourceId?: string
  datasources:
    | DataCatalogQuery['iris']['factTablesConnection']['edges'][number]['node'][]
    | undefined
  onSelectDatasource?: (sourceId: string) => void
}

export const DataCatalogSelector = ({
  showActions = false,
  sourceId,
  datasources,
  onSelectDatasource,
}: DataCatalogSelectorProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const selectedDatasource = useMemo(
    () => datasources?.find((source) => source.id === sourceId),
    [sourceId, datasources]
  )

  return (
    datasources && (
      <TwoColumnLayout
        sticky
        sideContent={
          datasources && (
            <Box minHeight="full">
              <DataCatalogList
                onSelectDatasource={onSelectDatasource}
                datasources={datasources}
                selectedDatasourceId={sourceId}
              />
            </Box>
          )
        }
        mainColumnPosition="right"
      >
        <Box className={classNames('flex flex-col', showActions && 'h-full')}>
          {/* TODO:Section Header padding fix solve in f0*/}
          <Box className="max-w-full overflow-x-hidden">
            <Box
              className="mb-4"
              style={showActions ? { marginLeft: '-28px', marginRight: '-28px' } : undefined}
            >
              <SectionHeader
                title={selectedDatasource?.name || ''}
                description={''}
                action={
                  showActions && sourceId
                    ? {
                        variant: 'default',
                        label: i18n.t('ai_reports.reports.new_exploration'),

                        icon: Add,
                        onClick: () =>
                          sourceId &&
                          navigate(
                            navigation.aiReports.catalog.slug.new.fullPath({
                              slug: getSlugFromTableId(sourceId),
                            })
                          ),
                      }
                    : undefined
                }
              />
            </Box>
          </Box>
          <Box
            className={classNames(
              'flex-1 flex flex-col overflow-y-auto min-h-0',
              !sourceId && 'align-center justify-center h-full'
            )}
          >
            {selectedDatasource?.description && (
              <Box className="grow">
                <CollapsibleTeaserBlock collapsedHeight={100}>
                  <RichTextDisplay
                    content={selectedDatasource?.description || ''}
                    format="markdown"
                  />
                </CollapsibleTeaserBlock>
              </Box>
            )}
            {sourceId ? (
              <Box className={classNames('flex-1 min-h-[200px] w-full', !showActions && 'px-6')}>
                <SourceDetail sourceId={sourceId} hideAttributes={!showActions} />
              </Box>
            ) : (
              <OneEmptyState
                title={i18n.t('ai_reports.data_catalog.empty_state.title')}
                description={i18n.t('ai_reports.data_catalog.empty_state.description')}
                emoji="🚀"
              />
            )}
          </Box>
        </Box>
      </TwoColumnLayout>
    )
  )
}
